import {
  clearBytes,
  decryptAttachmentBytes,
  encryptAttachmentVariants,
  type AttachmentRef,
  type CollectionRecord,
  type MemoryCard,
} from '@safewallet/core';
import {
  loadLocalAttachment,
  type AttachmentSlot,
  type LocalEncryptedAttachment,
} from './attachment-store';

export interface PreparedWalletMerge {
  cards: MemoryCard[];
  collections: CollectionRecord[];
  attachments: LocalEncryptedAttachment[];
}

type AttachmentLoader = (attachmentId: string) => Promise<LocalEncryptedAttachment | null>;

function exactArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function attachmentEnvelope(
  attachment: LocalEncryptedAttachment,
  variant: 'original' | 'thumbnail',
) {
  const nonce = variant === 'original' ? attachment.originalNonce : attachment.thumbnailNonce;
  const checksumSha256 = variant === 'original' ? attachment.originalChecksum : attachment.thumbnailChecksum;
  if (!nonce || !checksumSha256) {
    throw new Error(`The encrypted ${variant} metadata is incomplete.`);
  }
  return {
    cryptoVersion: attachment.cryptoVersion,
    wrappedAttachmentKey: attachment.wrappedAttachmentKey,
    keyNonce: attachment.keyNonce,
    nonce,
    checksumSha256,
  };
}

async function migrateAttachment(
  card: MemoryCard,
  ref: AttachmentRef,
  targetRecordId: string,
  sourceVaultKey: Uint8Array,
  targetVaultKey: Uint8Array,
  loadAttachment: AttachmentLoader,
): Promise<{ ref: AttachmentRef; attachment: LocalEncryptedAttachment }> {
  const source = await loadAttachment(ref.attachmentId);
  if (!source?.originalCiphertext) {
    throw new Error(
      `“${card.title}” has a photo or file that is not available on this device. `
      + 'Nothing was removed from the original wallet.',
    );
  }
  if (source.recordId !== card.recordId) {
    throw new Error(`The attachment for “${card.title}” does not belong to that item.`);
  }

  let originalPlaintext: Uint8Array | null = null;
  let thumbnailPlaintext: Uint8Array | null = null;
  try {
    originalPlaintext = await decryptAttachmentBytes(
      new Uint8Array(source.originalCiphertext),
      attachmentEnvelope(source, 'original'),
      sourceVaultKey,
      card.recordId,
      source.attachmentId,
      'original',
    );
    if (source.thumbnailCiphertext && source.thumbnailNonce && source.thumbnailChecksum) {
      thumbnailPlaintext = await decryptAttachmentBytes(
        new Uint8Array(source.thumbnailCiphertext),
        attachmentEnvelope(source, 'thumbnail'),
        sourceVaultKey,
        card.recordId,
        source.attachmentId,
        'thumbnail',
      );
    }

    const attachmentId = crypto.randomUUID();
    const encrypted = await encryptAttachmentVariants(
      originalPlaintext,
      thumbnailPlaintext ?? undefined,
      targetVaultKey,
      targetRecordId,
      attachmentId,
    );
    const timestamp = new Date().toISOString();
    const slot = (ref.slot ?? source.slot ?? 'note') as AttachmentSlot;
    const mimeType = source.mimeType || ref.mimeType || 'application/octet-stream';
    const attachment: LocalEncryptedAttachment = {
      attachmentId,
      recordId: targetRecordId,
      slot,
      cryptoVersion: encrypted.cryptoVersion,
      wrappedAttachmentKey: encrypted.wrappedAttachmentKey,
      keyNonce: encrypted.keyNonce,
      originalNonce: encrypted.original.nonce,
      originalChecksum: encrypted.original.checksumSha256,
      originalCiphertext: exactArrayBuffer(encrypted.original.ciphertext),
      thumbnailNonce: encrypted.thumbnail?.nonce,
      thumbnailChecksum: encrypted.thumbnail?.checksumSha256,
      thumbnailCiphertext: encrypted.thumbnail ? exactArrayBuffer(encrypted.thumbnail.ciphertext) : undefined,
      mimeType,
      sizeBytes: encrypted.original.ciphertext.byteLength,
      thumbnailSizeBytes: encrypted.thumbnail?.ciphertext.byteLength,
      state: 'local',
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    return {
      ref: {
        ...ref,
        attachmentId,
        slot,
        mimeType,
        sizeBytes: attachment.sizeBytes,
        state: 'local',
      },
      attachment,
    };
  } finally {
    await clearBytes(originalPlaintext);
    await clearBytes(thumbnailPlaintext);
  }
}

export async function prepareWalletMerge(
  cards: MemoryCard[],
  collections: CollectionRecord[],
  sourceVaultKey: Uint8Array,
  targetVaultKey: Uint8Array,
  loadAttachment: AttachmentLoader = loadLocalAttachment,
): Promise<PreparedWalletMerge> {
  const collectionIdMap = new Map<string, string>();
  const copiedCollections = collections.map((collection) => {
    const recordId = crypto.randomUUID();
    collectionIdMap.set(collection.recordId, recordId);
    return { ...collection, recordId };
  });

  const copiedCards: MemoryCard[] = [];
  const copiedAttachments: LocalEncryptedAttachment[] = [];
  for (const card of cards) {
    const recordId = crypto.randomUUID();
    const attachmentRefs: AttachmentRef[] = [];
    for (const ref of card.attachmentRefs) {
      const migrated = await migrateAttachment(
        card,
        ref,
        recordId,
        sourceVaultKey,
        targetVaultKey,
        loadAttachment,
      );
      attachmentRefs.push(migrated.ref);
      copiedAttachments.push(migrated.attachment);
    }
    copiedCards.push({
      ...card,
      recordId,
      collectionIds: card.collectionIds
        .map((id) => collectionIdMap.get(id))
        .filter((id): id is string => Boolean(id)),
      attachmentRefs,
    });
  }

  return {
    cards: copiedCards,
    collections: copiedCollections,
    attachments: copiedAttachments,
  };
}
